import { useState } from 'react';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

type ClassroomResult = {
  id: string;
  public_name?: string | null;
  is_confirmed?: boolean | null;
};

type DonateShareCodeInputProps = {
  onResolved: (c: ClassroomResult) => void;
};

export default function DonateShareCodeInput({ onResolved }: DonateShareCodeInputProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const resolve = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${CORE_API}/api/v1/community/share-resolve?code=${encodeURIComponent(trimmed)}`);
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Unable to resolve code');
      }
      const raw = await response.json();
      const data: ClassroomResult = raw.data ?? raw;
      onResolved(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Not found';
      setError(message || 'Not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-sand bg-white/70 p-3">
      <label className="text-sm font-semibold text-cocoa">Have a classroom share code?</label>
      <div className="mt-2 flex gap-2">
        <input
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
          className="flex-1 rounded-xl border px-3 py-2"
          placeholder="PT-8KQ2"
          autoComplete="off"
        />
        <button
          onClick={resolve}
          disabled={loading || !code.trim()}
          className="rounded-xl bg-brick px-3 py-2 text-white disabled:opacity-60"
          type="button"
        >
          {loading ? 'Checking…' : 'Resolve'}
        </button>
      </div>
      {error ? <p className="mt-2 text-xs text-brick">{error}</p> : null}
    </div>
  );
}
